import { useMemo, useState } from 'react';
import {
    NSCard, NSDatePicker, NSInputNumberStepper, NSLabel,
} from 'bricks';
import { format, parseISO } from 'date-fns';
import {
    FORM_LABEL_CLASS_NAME, HOLD_PERIOD_TOOLTIP_TEXT, PROJECT_ACTUALS_ICON_WITH_TOOLTIP_TEXT, ProjectActualsDiscrepanciesEnum,
} from 'views/ProFormaTable/constants';
import RenderIf from 'components/RenderIf/RenderIf';
import { IProFormaValues } from 'views/ProFormaTable/types';
import MetricOutputCard from 'views/ProFormaTable/components/MetricOutputCard/MetricOutputCard';
import { ProFormaQuickStartValues } from 'views/public/ProFormaQuickStart/types';
import { faCircleInfo, faMemoCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { isLandDevelopmentModel } from 'helpers/proForma';
import OverwriteBudgetDatesModal from './OverwriteBudgetDatesModal/OverwriteBudgetDatesModal';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import { buildSalesStartOptions, resolveSalesStartOption } from '../../ProFormaDisposition/helpers';

interface ProFormaKeyDatesProps {
    shouldUseProjectActuals: boolean;
    actualsDiscrepancies?: ProjectActualsDiscrepanciesEnum[];
    values: IProFormaValues;
    handleInputChange: (name: string, value: string) => void;
    handleDateChange: (
        field: string,
        value: string,
        handleUpdateProForma: (keyValues: any[], successCallback?: () => void) => void,
        overwriteBudgetDates: boolean
    ) => void;
    handleUpdateProForma: (keyValues: any[], successCallback?: () => void) => void;
    proFormaSignUpData?: ProFormaQuickStartValues;
}

const ProFormaKeyDates = ({
    shouldUseProjectActuals,
    actualsDiscrepancies,
    values,
    handleInputChange,
    handleDateChange,
    handleUpdateProForma,
    proFormaSignUpData,
}: ProFormaKeyDatesProps) => {
    const { proForma, uses, operatingInfo, disposition } = useProFormaSocketContext();

    const parseDate = (dateValue: any): Date => {
        if (typeof dateValue === 'string') {
            return parseISO(dateValue);
        }
        return new Date(dateValue);
    };

    const [showOverwriteModal, setShowOverwriteModal] = useState(false);
    const [updatedField, setUpdatedField] = useState<string | undefined>(undefined);

    const toggleOverwriteModal = () => setShowOverwriteModal(!showOverwriteModal);

    const handleUpdateKeyDateField = (shouldOverwriteBudgetLineDates: boolean) => {
        if (updatedField === 'startDate') {
            handleDateChange('startDate', values.startDate, handleUpdateProForma, shouldOverwriteBudgetLineDates);
        } else if (updatedField === 'constructionStart') {
            handleDateChange('constructionStart', values.constructionStart || '', handleUpdateProForma, shouldOverwriteBudgetLineDates);
        } else if (updatedField === 'constructionDuration') {
            const currentHoldPeriod = proForma.holdPeriod;

            handleUpdateProForma(
                [
                    { key: 'constructionDuration', value: String(values.constructionDuration) },
                    { key: 'overwriteBudgetDates', value: shouldOverwriteBudgetLineDates },
                ],
                () => {
                    if (currentHoldPeriod) {
                        handleUpdateProForma([{ key: 'holdPeriod', value: String(currentHoldPeriod) }]);
                    }
                },
            );
        }
    };

    const handleCancel = () => {
        if (updatedField === 'startDate') {
            handleInputChange('startDate', proForma.startDate);
        } else if (updatedField === 'constructionStart') {
            handleInputChange('constructionStart', proForma.constructionStart);
        } else if (updatedField === 'constructionDuration') {
            handleInputChange('constructionDuration', String(proForma.constructionDuration));
        }
        setUpdatedField(undefined);
    };

    const areBudgetLineDatesOutOfBounds = useMemo(() => {
        const isDevStartOutOfBounds = new Date(values.startDate) > new Date(uses.developmentStartDate!);
        const isDevEndOutOfBounds = new Date(values.saleDate) < new Date(uses.developmentEndDate!);
        return isDevStartOutOfBounds || isDevEndOutOfBounds;
    }, [values.startDate, values.saleDate, uses.developmentStartDate, uses.developmentEndDate]);

    const isLandDev = isLandDevelopmentModel(proForma.proFormaModelId);
    const landDevSaleMonth = isLandDev
        ? resolveSalesStartOption(
            buildSalesStartOptions(proForma?.constructionStart, uses?.developmentEndDate, disposition?.saleFrequency ?? 'quarterly'),
            disposition?.salesStart,
        )?.value ?? null
        : null;

    return (
        <>
            <NSCard className="p-3 NSCard--level-2">
                <div className="d-flex flex-row w-100 align-items-start">
                    <div className="d-flex flex-column w-50">
                        <div className="d-flex pb-3 w-100">
                            <div className="d-flex col-4 pl-0">
                                <RenderIf isTrue={
                                    shouldUseProjectActuals
                                    && actualsDiscrepancies?.includes(ProjectActualsDiscrepanciesEnum.PRO_FORMA_START_DATE)
                                }
                                >
                                    <IconWithTooltip
                                        name={ProjectActualsDiscrepanciesEnum.PRO_FORMA_START_DATE}
                                        icon={faMemoCircleCheck}
                                        tooltipText={PROJECT_ACTUALS_ICON_WITH_TOOLTIP_TEXT}
                                        className="my-auto mr-1"
                                    />
                                </RenderIf>
                                <NSLabel className={FORM_LABEL_CLASS_NAME}>Start month</NSLabel>
                            </div>
                            <NSDatePicker
                                name="startDate"
                                value={values.startDate ? parseDate(values.startDate) : null}
                                onChange={(date: Date | null) => {
                                    if (date) {
                                        handleInputChange('startDate', format(date, 'yyyy-MM'));
                                    }
                                }}
                                showMonthYearPicker
                                dateFormat="MMMM yyyy"
                                onBlur={() => {
                                    if (!proFormaSignUpData) {
                                        setUpdatedField('startDate'); toggleOverwriteModal();
                                    }
                                }}
                            />
                        </div>
                        <div className="d-flex w-100 pb-3">
                            <NSLabel className={FORM_LABEL_CLASS_NAME}>Construction start</NSLabel>
                            <NSDatePicker
                                name="constructionStart"
                                value={values.constructionStart ? parseDate(values.constructionStart) : null}
                                onChange={(date: Date | null) => {
                                    if (date) {
                                        handleInputChange('constructionStart', format(date, 'yyyy-MM'));
                                    }
                                }}
                                showMonthYearPicker
                                dateFormat="MMMM yyyy"
                                minDate={values.startDate ? parseDate(values.startDate) : undefined}
                                maxDate={values.saleDate ? parseDate(values.saleDate) : undefined}
                                onBlur={() => {
                                    if (!proFormaSignUpData) {
                                        setUpdatedField('constructionStart'); toggleOverwriteModal();
                                    }
                                }}
                            />
                        </div>
                        <div className="d-flex w-100 pb-3">
                            <NSLabel className={FORM_LABEL_CLASS_NAME}>Construction duration</NSLabel>
                            <NSInputNumberStepper
                                name="constructionDuration"
                                value={values.constructionDuration || null}
                                onChange={(count: number) => {
                                    handleInputChange('constructionDuration', String(count));
                                    if (!proFormaSignUpData) {
                                        setUpdatedField('constructionDuration');
                                        toggleOverwriteModal();
                                    }
                                }}
                                customSuffix={(values.constructionDuration || 0) > 1 ? 'months' : 'month'}
                                min={1}
                                max={600}
                                step={1}
                            />
                        </div>
                        <div className="d-flex w-100">
                            <div className="d-flex col-4 pl-0">
                                <RenderIf isTrue={
                                    shouldUseProjectActuals
                                    && actualsDiscrepancies?.includes(ProjectActualsDiscrepanciesEnum.HOLD_PERIOD)
                                }
                                >
                                    <IconWithTooltip
                                        name={ProjectActualsDiscrepanciesEnum.HOLD_PERIOD}
                                        icon={faMemoCircleCheck}
                                        tooltipText={PROJECT_ACTUALS_ICON_WITH_TOOLTIP_TEXT}
                                        className="my-auto mr-1"
                                    />
                                </RenderIf>
                                <NSLabel className="pl-0 my-auto font-weight-normal text-dark text-nowrap">Hold period</NSLabel>
                                <IconWithTooltip
                                    name="holdPeriodTooltip"
                                    icon={faCircleInfo}
                                    tooltipText={HOLD_PERIOD_TOOLTIP_TEXT}
                                    className="my-auto text-dark ml-1"
                                />
                            </div>
                            <NSInputNumberStepper
                                name="holdPeriod"
                                value={values.holdPeriod || null}
                                onChange={(count: number) => {
                                    handleInputChange('holdPeriod', String(count));
                                    handleUpdateProForma([{ key: 'holdPeriod', value: String(count) }]);
                                }}
                                customSuffix={(values.holdPeriod || 0) > 1 ? 'months' : 'month'}
                                min={(operatingInfo.operationBeginMonth || 0) + 2}
                                max={600}
                                step={1}
                            />
                        </div>
                    </div>
                    <div className="d-flex flex-column justify-content-start ml-3 w-50">
                        <MetricOutputCard
                            label="Sale month"
                            value={isLandDev
                                ? (landDevSaleMonth ? format(parseDate(landDevSaleMonth), 'MMMM yyyy') : '')
                                : (values.saleDate && values.saleMonth
                                    ? `${format(parseDate(values.saleDate), 'MMMM yyyy')} (Month ${values.saleMonth})` : '')}
                            showInProgress={isLandDev ? !landDevSaleMonth : !values.saleDate}
                        />
                    </div>
                </div>
            </NSCard>

            <RenderIf isTrue={showOverwriteModal}>
                <OverwriteBudgetDatesModal
                    isOpen={showOverwriteModal}
                    toggle={toggleOverwriteModal}
                    handleCancel={handleCancel}
                    handleConfirm={handleUpdateKeyDateField}
                    areBudgetLineDatesOutOfBounds={areBudgetLineDatesOutOfBounds}
                />
            </RenderIf>
        </>
    );
};

export default ProFormaKeyDates;
